import React, { useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Link, Typography } from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import styled from "@emotion/styled";
import { formatDateTime } from "../utils/dateUtils";

const TitleRow = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const TitleText = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    lineHeight: 1.2,
}));

const TopAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const StatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
}));

const ContentBox = styled(Box)(({ theme }) => ({
    overflowWrap: 'anywhere',
    '& h1, & h2, & h3, & h4, & h5, & h6': {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        lineHeight: 1.3,
    },
    '& h1': { fontSize: theme.typography.size.xl, fontWeight: theme.typography.fontWeightSemiBold },
    '& h2': { fontSize: theme.typography.size.lg, fontWeight: theme.typography.fontWeightSemiBold },
    '& h3': { fontSize: '1.1rem', fontWeight: theme.typography.fontWeightSemiBold },
    '& h4, & h5, & h6': { fontSize: theme.typography.size.base, fontWeight: theme.typography.fontWeightSemiBold },
    '& p': {
        marginBottom: theme.spacing(1),
        lineHeight: 1.6,
    },
    '& ul, & ol': {
        paddingLeft: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },
    '& li': {
        marginBottom: theme.spacing(0.5),
    },
    '& hr': {
        border: 'none',
        borderTop: `1px solid ${theme.palette.divider}`,
        margin: theme.spacing(2, 0),
    },
    '& blockquote': {
        borderLeft: `3px solid ${theme.palette.divider}`,
        paddingLeft: theme.spacing(2),
        marginLeft: 0,
        color: theme.palette.text.secondary,
    },
    '& code': {
        fontFamily: 'monospace',
        fontSize: theme.typography.size.md,
        backgroundColor: theme.palette.action.hover,
        padding: theme.spacing(0.25, 0.75),
        borderRadius: theme.shape.borderRadius,
    },
    '& pre': {
        backgroundColor: theme.palette.action.hover,
        padding: theme.spacing(1.5),
        borderRadius: theme.shape.borderRadius,
        overflowX: 'auto',
        overflowWrap: 'normal',
        marginBottom: theme.spacing(1),
    },
    '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
    },
}));

const TableWrap = styled(Box)(({ theme }) => ({
    overflowX: 'auto',
    marginBottom: theme.spacing(1),
}));

const MarkdownTable = styled('table')(({ theme }) => ({
    borderCollapse: 'collapse',
    width: '100%',
    overflowWrap: 'normal',
    fontSize: theme.typography.size.md,
    '& th, & td': {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.75, 1),
        textAlign: 'left',
        verticalAlign: 'top',
    },
    '& th': {
        backgroundColor: theme.palette.action.hover,
        fontWeight: theme.typography.fontWeightSemiBold,
    },
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const markdownComponents = {
    table: ({ node, ...props }) => (
        <TableWrap>
            <MarkdownTable {...props} />
        </TableWrap>
    ),
    a: ({ node, ...props }) => <Link target="_blank" rel="noopener noreferrer" {...props} />,
};

export const AIMarkdown = ({ children }) => (
    <ContentBox>
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
            {children || ''}
        </ReactMarkdown>
    </ContentBox>
);

const GeneratedCaption = styled(Typography)(({ theme }) => ({
    display: 'block',
    marginTop: theme.spacing(2),
}));

const formatRemaining = (msLeft) => {
    if (!(msLeft > 0)) return 'no longer available';
    const totalSeconds = Math.floor(msLeft / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `available for another ${hours}h ${minutes}m`;
    if (minutes > 0) return `available for another ${minutes}m ${seconds}s`;
    return `available for another ${seconds}s`;
};

const readyCaption = (record, msLeft) => {
    const parts = [];
    const generated = formatDateTime(record.completedAt);
    if (generated) parts.push(`Generated ${generated}`);
    const expired = msLeft !== null && msLeft <= 0;
    const expires = formatDateTime(record.expiresAt);
    if (expires && !expired) parts.push(`available until ${expires}`);
    if (msLeft !== null) parts.push(formatRemaining(msLeft));
    return parts.join(' · ');
};

const AIResponseDialog = ({ open, onClose, title, subtitle, record, busy, error, onRetry, action, icon = <AutoAwesome fontSize="small" />, children }) => {
    const [msLeft, setMsLeft] = useState(null);
    const expiresAt = record?.status === 'ready' ? record.expiresAt : null;

    useEffect(() => {
        if (!open || !expiresAt) {
            setMsLeft(null);
            return undefined;
        }
        const expiry = new Date(expiresAt).getTime();
        if (Number.isNaN(expiry)) {
            setMsLeft(null);
            return undefined;
        }
        let timer = null;
        const tick = () => {
            const remaining = expiry - Date.now();
            setMsLeft(remaining);
            if (remaining <= 0 && timer) {
                clearInterval(timer);
                timer = null;
            }
        };
        tick();
        if (expiry > Date.now()) timer = setInterval(tick, 1000);
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [open, expiresAt]);

    const caption = record?.status === 'ready' ? readyCaption(record, msLeft) : '';

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <TitleRow>
                {icon}
                <TitleText>
                    <Box component="span">{title}</Box>
                    {subtitle && (
                        <Subtitle variant="caption" color="text.secondary">{subtitle}</Subtitle>
                    )}
                </TitleText>
            </TitleRow>
            <DialogContent dividers>
                {error && (
                    <TopAlert severity="error">{error}</TopAlert>
                )}
                {busy && !record && (
                    <StatusRow>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Checking status…</Typography>
                    </StatusRow>
                )}
                {record?.status === 'pending' && (
                    <StatusRow>
                        <CircularProgress size={18} />
                        <Box>
                            <Typography variant="body2">Your request is being processed by the AI.</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Requested {formatDateTime(record.requestedAt)}. You can close this dialog — we'll notify you when the summary is ready.
                            </Typography>
                        </Box>
                    </StatusRow>
                )}
                {record?.status === 'ready' && (
                    <Box>
                        <AIMarkdown>{record.content}</AIMarkdown>
                        {caption && (
                            <GeneratedCaption variant="caption" color="text.secondary">
                                {caption}
                            </GeneratedCaption>
                        )}
                    </Box>
                )}
                {record?.status === 'failed' && (
                    <Alert severity="warning">
                        <AlertTitle>The AI couldn't finish this one</AlertTitle>
                        {record.error ? `${record.error}. ` : ''}
                        {onRetry ? 'Use Try again below to run it once more.' : 'Nothing is needed from you — the next one arrives on its usual schedule.'}
                    </Alert>
                )}
                {children}
            </DialogContent>
            <DialogActions>
                {record?.status === 'failed' && onRetry && (
                    <Button onClick={onRetry} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : <AutoAwesome />}>
                        Try again
                    </Button>
                )}
                {action}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AIResponseDialog;
